import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useApi, useToaster, type AdminUpdateReviewBody } from '@shopexperts/shared';

interface UpdateAdminReviewParams {
  reviewId: number;
  payload: AdminUpdateReviewBody;
}

export function useUpdateAdminReview() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['admin', 'updateReview'],
    mutationFn: async ({ reviewId, payload }: UpdateAdminReviewParams) => {
      return await api.admin.reviewsV2.updateReview({
        params: { review: reviewId },
        body: payload,
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to update review. Please try again.';

      toaster.create({
        title: 'Error updating review',
        description: errorMessage,
        type: 'error',
      });

      console.error('[useUpdateAdminReview] Error:', error);
    },
    onSuccess: () => {
      toaster.create({
        title: 'Review updated successfully',
        description: 'The review has been updated.',
        type: 'success',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews', 'published'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews', 'all'] });
    },
  });

  const updateReview = (reviewId: number, payload: AdminUpdateReviewBody) => {
    return mutation.mutateAsync({ reviewId, payload });
  };

  return {
    updateReview,
    updateReviewAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}
